import { API_BASE_URL } from './config';
import { AuthService } from './AuthService';
import { authEmitter } from './emitter';
import { willExpireSoon } from './decodeJwt';

let needsPasswordChange = false;
let currentAccess: string | null = null;
let currentRefresh: string | null = null;

authEmitter.on(evt => {
  if (evt.type === 'login') {
    needsPasswordChange = evt.state.needsPasswordChange;
    currentAccess = evt.state.access;
    currentRefresh = evt.state.refresh;
  } else if (evt.type === 'tokens') {
    currentAccess = evt.access;
    currentRefresh = evt.refresh;
  } else if (evt.type === 'passwordChanged') {
    needsPasswordChange = false;
  } else if (evt.type === 'logout') {
    needsPasswordChange = false; currentAccess = null; currentRefresh = null;
  }
});

interface FetchOptions extends RequestInit { retry?: boolean; }

const inFlightGetRequests = new Map<string, Promise<Response>>();
const rateLimitUntilByRequest = new Map<string, number>();

function getRetryAfterMs(response: Response): number {
  const retryAfterRaw = response.headers.get('Retry-After');
  const retryAfterSeconds = Number(retryAfterRaw || 0);
  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    return retryAfterSeconds * 1000;
  }
  return 30000;
}

function normalizePath(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    if (API_BASE_URL && !path.startsWith(API_BASE_URL)) throw new Error('Cross-domain requests are disallowed');
    return path; // already absolute to our base
  }
  if (!path.startsWith('/')) path = `/${path}`;
  // When API_BASE_URL is empty, use relative paths for nginx/CRA proxy
  if (!API_BASE_URL) {
    return path; // e.g. /api/auth/login (proxied by nginx or CRA)
  }
  return `${API_BASE_URL}${path}`;
}

export async function authorizedFetch(path: string, init: FetchOptions = {}): Promise<Response> {
  if (needsPasswordChange && !path.includes('/api/auth/change-password')) {
    throw new Error('Password change required before making other requests');
  }
  // Proactive refresh if token near expiry
  if (willExpireSoon(currentAccess) && currentRefresh) {
    try { await AuthService.refreshAccess(); } catch { /* will clear state if invalid */ }
    currentAccess = AuthService.getAccess();
  }
  const headers: Record<string,string> = {};
  if (currentAccess) headers['Authorization'] = `Bearer ${currentAccess}`;
  if (init.headers) {
    if (init.headers instanceof Headers) init.headers.forEach((v,k)=> headers[k]=v);
    else Object.assign(headers, init.headers as any);
  }
  const url = normalizePath(path);
  const method = (init.method || 'GET').toUpperCase();
  const requestKey = `${method} ${url}`;

  const cooldownUntil = rateLimitUntilByRequest.get(requestKey) || 0;
  if (Date.now() < cooldownUntil) {
    return new Response(null, { status: 429, statusText: 'Too Many Requests' });
  }

  if (method === 'GET') {
    const existingPromise = inFlightGetRequests.get(requestKey);
    if (existingPromise) {
      const sharedResponse = await existingPromise;
      return sharedResponse.clone();
    }
  }

  let res: Response;
  const requestPromise = (async () => {
    try {
      return await fetch(url, { ...init, headers });
    } catch (e: any) {
      const err: any = new Error('Network error: failed to reach API');
      err.cause = e;
      throw err;
    }
  })();

  if (method === 'GET') {
    inFlightGetRequests.set(requestKey, requestPromise);
  }

  try {
    res = await requestPromise;
  } finally {
    if (method === 'GET') {
      inFlightGetRequests.delete(requestKey);
    }
  }

  if (res.status === 429) {
    const retryMs = getRetryAfterMs(res);
    rateLimitUntilByRequest.set(requestKey, Date.now() + retryMs);
  }

  if (res.status === 401 && !init.retry) {
    // attempt single refresh then retry
    try {
      await AuthService.refreshAccess();
      currentAccess = AuthService.getAccess();
      const retryHeaders = { ...headers, Authorization: currentAccess ? `Bearer ${currentAccess}` : '' };
      return authorizedFetch(path, { ...init, headers: retryHeaders, retry: true });
    } catch {
      // cleared state already
      throw new Error('Unauthorized - session expired');
    }
  }
  return method === 'GET' ? res.clone() : res;
}
